import { toastManager } from '@/app/ui/components/toast';
import {
  analyseBid,
  deleteBid,
  getBidDetails,
  getBids,
  updateBid,
} from '@/app/services/bidsService';
import { UpdateBidRequest } from '@/app/types/Dtos/request';
import { GetBidsResponse } from '@/app/types/Dtos/response';
import { mutationKeys, queryKeys } from '@/app/utils/constants';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { useMemo } from 'react';
import { useParams } from '@tanstack/react-router';

export const useAnalyseBidDocuments = () => {
  const queryClient = useQueryClient();
  const t = useTranslations('general.notifications');
  const { tenderPackageId } = useParams({
    strict: false,
  });

  return useMutation({
    mutationKey: [mutationKeys.ANALYSE_BID_DOCUMENTS],
    mutationFn: async (bidId: string) => {
      const res = await analyseBid(bidId);
      return { res, bidId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.BIDS, tenderPackageId] });
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('errorAnalysisRedirecting'),
      });
    },
  });
};

export const useGetBidsList = (tenderPackageId: string, initialData?: GetBidsResponse) => {
  return useQuery({
    queryKey: [queryKeys.BIDS, tenderPackageId],
    queryFn: async () => {
      const { data } = await getBids({
        tenderPackageId,
        page: 1,
        pageSize: 1000,
        orderBy: 'CreatedAt',
        orderDirection: 'desc',
      });
      return data;
    },
    initialData,
  });
};

export const getBidDetailsQuery = (bidId: string) =>
  queryOptions({
    queryKey: [queryKeys.BID_DETAILS, bidId],
    queryFn: async () => {
      const { data } = await getBidDetails(bidId);
      return data;
    },
  });

export const useUpdateBidMutation = (bidId: string, tenderPackageId: string) => {
  const t = useTranslations('general.notifications');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBidRequest) => {
      await updateBid(bidId, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.BID_DETAILS, bidId],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.BIDS, tenderPackageId],
      });
    },
    onError: () => {
      toastManager.add({
        type: 'error',
        title: t('error'),
      });
    },
  });
};

export const useDeleteBid = () => {
  return useMutation({
    mutationFn: async (bidId: string) => {
      await deleteBid(bidId);
    },
  });
};

export const useGetAllBidDocuments = (tenderPackageId: string) => {
  const { data: bidsData } = useGetBidsList(tenderPackageId);
  const bidIds = useMemo(() => bidsData?.data?.map((bid) => bid.id) || [], [bidsData]);

  const bidDetailsQueries = useQueries({
    queries: bidIds.map((bidId) => ({
      queryKey: [queryKeys.BID_DETAILS, bidId],
      queryFn: async () => {
        const { data } = await getBidDetails(bidId);
        return data;
      },
      enabled: !!bidId,
    })),
  });

  const allDocuments = useMemo(
    () => bidDetailsQueries.flatMap((query) => query.data?.documents || []),
    [bidDetailsQueries],
  );

  return allDocuments;
};
